import { useAuth as useClerkAuth, useClerk } from '@clerk/clerk-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { matchPath, useLocation, type NavigateFunction } from 'react-router-dom';
import { emitAuthTrace } from '@/auth/authTraceClient';
import { resolveAuthBootstrapState, resolvePostAuthRedirect } from '@/auth/bootstrapRouting';
import { emitAuthTransitionEvent, recordAuthBootstrapSample } from '@/auth/observability';
import {
  consumePostAuthRedirectAttempt,
  ensurePostAuthRedirectAttempt,
} from '@/auth/postAuthRedirectGuard';
import { useRecoveryUxPhase } from '@/auth/recoveryUxPhase';
import {
  getManualSignOutFlag,
  getSignOutTransitionFlag,
  getStoredSession,
} from '@/auth/sessionStorage';
import { hasClerkClientSession } from '@/auth/useAuth.helpers';
import { AUTH_ROUTE_PATHS } from '@/navigation/routes/authRoutePaths';
import type { AuthError, AuthSession } from '@/auth/types';
import type { AppRouteDefinition } from '@/navigation/routes/routes';

type UseAppAuthBootstrapFlowParams = {
  session: AuthSession | null;
  authLoading: boolean;
  authError: AuthError | null;
  activePath: string;
  routeDefinitions: AppRouteDefinition[];
  navigate: NavigateFunction;
};

export const isEntryAuthFlowPath = (activePath: string): boolean =>
  activePath.startsWith('/sign-in') ||
  activePath.startsWith('/sign-up') ||
  activePath.startsWith(AUTH_ROUTE_PATHS.acceptInvitation) ||
  activePath === '/auth/callback' ||
  activePath === '/reset-password' ||
  activePath.startsWith('/reset-password/') ||
  activePath.startsWith('/verify-email/');

type UseAppAuthBootstrapFlowResult = {
  isEntryAuthFlow: boolean;
  isProtectedPath: boolean;
  authBootstrapState: ReturnType<typeof resolveAuthBootstrapState>;
  recoveryPhase: ReturnType<typeof useRecoveryUxPhase>;
  onboardingIncomplete: boolean;
  showAuthenticatedShell: boolean;
  shouldBlockRoutesDuringRecovery: boolean;
};

export const useAppAuthBootstrapFlow = ({
  session,
  authLoading,
  authError,
  activePath,
  routeDefinitions,
  navigate,
}: UseAppAuthBootstrapFlowParams): UseAppAuthBootstrapFlowResult => {
  const location = useLocation();
  const { isSignedIn: isClerkSignedIn } = useClerkAuth();
  const { client } = useClerk();
  const isEntryAuthFlow = isEntryAuthFlowPath(activePath);
  const isProtectedPath = useMemo(
    () =>
      routeDefinitions.some((route) => {
        if (!(route.requiresAuth || route.requiresAdmin || route.requiresSuperuser)) {
          return false;
        }
        return matchPath({ path: route.path, end: false }, activePath) !== null;
      }),
    [activePath, routeDefinitions],
  );
  const manualSignOut = getManualSignOutFlag();
  const signOutTransition = getSignOutTransitionFlag();
  const hasRecoverableSessionSignal = [
    isClerkSignedIn,
    hasClerkClientSession(client as unknown),
  ].some((value) => value);
  const authBootstrapState = resolveAuthBootstrapState({
    session,
    authLoading,
    manualSignOut,
    hasRecoverableSessionSignal,
    hasTerminalAuthError: Boolean(authError && !session),
  });
  const postAuthRedirectPath = resolvePostAuthRedirect({
    intendedPath: activePath,
    bootstrapState: authBootstrapState,
    fallback: '/execution-runs',
  });
  const onboardingIncomplete =
    session !== null &&
    !isEntryAuthFlow &&
    postAuthRedirectPath === '/onboarding' &&
    activePath !== '/onboarding';
  const recoveryPhase = useRecoveryUxPhase(authBootstrapState === 'recovering');
  const recoveryStartedAtRef = useRef<number | null>(null);
  const recoveryTimeoutEmittedRef = useRef(false);
  const recoverySignInRedirectedRef = useRef(false);
  const previousBootstrapStateRef = useRef(authBootstrapState);
  const shouldRedirectPostAuth =
    session !== null && !authLoading && !isEntryAuthFlow && activePath !== postAuthRedirectPath;
  const [postAuthAttemptId, setPostAuthAttemptId] = useState<string | null>(null);
  useEffect(() => {
    if (!session) {
      setPostAuthAttemptId(null);
      return;
    }
    setPostAuthAttemptId(ensurePostAuthRedirectAttempt(session));
  }, [session]);

  useEffect(() => {
    if (!shouldRedirectPostAuth) return;
    if (!postAuthAttemptId) return;
    emitAuthTrace({
      event: 'app_post_auth_redirect_check',
      userId: session.userId,
      detail: {
        activePath,
        postAuthRedirectPath,
      },
    });
    const shouldNavigate = consumePostAuthRedirectAttempt({
      attemptId: postAuthAttemptId,
      userId: session.userId,
    });
    if (!shouldNavigate) return;
    emitAuthTrace({
      event: 'app_post_auth_redirect_navigate',
      userId: session.userId,
      detail: {
        destination: postAuthRedirectPath,
      },
    });
    void navigate(postAuthRedirectPath, { replace: true, state: location.state });
  }, [
    activePath,
    location.state,
    navigate,
    postAuthAttemptId,
    postAuthRedirectPath,
    session,
    shouldRedirectPostAuth,
  ]);

  useEffect(() => {
    if (isEntryAuthFlow) {
      recoveryStartedAtRef.current = null;
      recoveryTimeoutEmittedRef.current = false;
      recoverySignInRedirectedRef.current = false;
      return;
    }
    if (authBootstrapState !== 'recovering') {
      recoveryStartedAtRef.current = null;
      recoveryTimeoutEmittedRef.current = false;
      recoverySignInRedirectedRef.current = false;
      return;
    }
    if (recoveryStartedAtRef.current !== null) return;
    recoveryStartedAtRef.current = Date.now();
    emitAuthTransitionEvent('auth_recovery_started', {
      from: activePath,
      to: activePath,
      reason: 'app_bootstrap_recovering',
      userId: session?.userId ?? getStoredSession()?.userId ?? null,
    });
  }, [activePath, authBootstrapState, isEntryAuthFlow, session?.userId]);

  useEffect(() => {
    if (isEntryAuthFlow) return;
    if (!isProtectedPath) return;
    const shouldRedirectToSignIn =
      recoveryPhase === 'stalled' || authBootstrapState === 'error_terminal';
    if (!shouldRedirectToSignIn) return;
    if (recoverySignInRedirectedRef.current) return;
    recoverySignInRedirectedRef.current = true;
    void navigate('/sign-in?error=auth_timeout', { replace: true });
  }, [authBootstrapState, isEntryAuthFlow, isProtectedPath, navigate, recoveryPhase]);

  useEffect(() => {
    if (recoveryPhase !== 'stalled') return;
    if (recoveryTimeoutEmittedRef.current) return;
    const startedAt = recoveryStartedAtRef.current;
    if (startedAt === null) return;
    recoveryTimeoutEmittedRef.current = true;
    emitAuthTransitionEvent('auth_recovery_timeout', {
      from: activePath,
      to: '/sign-in',
      reason: 'app_bootstrap_recovery_stalled',
      durationMs: Date.now() - startedAt,
      userId: session?.userId ?? getStoredSession()?.userId ?? null,
    });
    recordAuthBootstrapSample({
      durationMs: Date.now() - startedAt,
      outcome: 'timeout',
    });
  }, [activePath, recoveryPhase, session?.userId]);

  useEffect(() => {
    if (authBootstrapState !== 'error_terminal') return;
    if (recoveryTimeoutEmittedRef.current) return;
    const startedAt = recoveryStartedAtRef.current;
    if (startedAt === null) return;
    recoveryTimeoutEmittedRef.current = true;
    emitAuthTransitionEvent('auth_recovery_timeout', {
      from: activePath,
      to: '/sign-in',
      reason: 'app_bootstrap_error_terminal',
      durationMs: Date.now() - startedAt,
      userId: session?.userId ?? getStoredSession()?.userId ?? null,
    });
    recordAuthBootstrapSample({
      durationMs: Date.now() - startedAt,
      outcome: 'timeout',
    });
  }, [activePath, authBootstrapState, session?.userId]);

  useEffect(() => {
    const previousState = previousBootstrapStateRef.current;
    previousBootstrapStateRef.current = authBootstrapState;
    if (previousState !== 'recovering') return;
    if (
      authBootstrapState !== 'authenticated_onboarding' &&
      authBootstrapState !== 'authenticated_ready'
    )
      return;
    const startedAt = recoveryStartedAtRef.current;
    if (startedAt === null) return;
    const durationMs = Date.now() - startedAt;
    emitAuthTransitionEvent('auth_recovery_completed', {
      from: activePath,
      to: activePath,
      reason: 'app_bootstrap_recovered',
      durationMs,
      userId: session?.userId ?? null,
    });
    recordAuthBootstrapSample({ durationMs, outcome: 'success' });
  }, [activePath, authBootstrapState, session?.userId]);

  const shouldHoldAuthenticatedShell =
    !manualSignOut &&
    !session &&
    !isEntryAuthFlow &&
    isProtectedPath &&
    authBootstrapState === 'recovering';
  const shouldKeepAuthenticatedShellForSignOut = signOutTransition && !session && isEntryAuthFlow;
  const showAuthenticatedShell =
    shouldKeepAuthenticatedShellForSignOut ||
    (!isEntryAuthFlow && (Boolean(session) || shouldHoldAuthenticatedShell));
  const shouldBlockRoutesDuringRecovery =
    !isEntryAuthFlow && isProtectedPath && authBootstrapState === 'error_terminal';

  return {
    isEntryAuthFlow,
    isProtectedPath,
    authBootstrapState,
    recoveryPhase,
    onboardingIncomplete,
    showAuthenticatedShell,
    shouldBlockRoutesDuringRecovery,
  };
};
